/** Agent workspace page segments under `/{tabKey}/`. */
export const TAB_EVENTS_PAGE = 'events';
export const TAB_WAITING_PAGE = 'waiting';
export const TAB_EVENT_ID_PAGE = 'event-id';
export const TAB_SITE_CHECK_PAGE = 'site-check';
export const TAB_PATROL_PAGE = 'patrol';
export const TAB_LOAD_PAGE = 'load';

const TAB_KEY_PATTERN = /^t\d+$/;

export type TabProcessingRouteMode = 'process-event' | 'site-check' | 'manual-patrol';

/** Parses `/t1/events` or `/t2/event-id/1` into tab key and persisted route path. */
export function parseAgentTabRoute(url: string): { tabId: string; routePath: string } | null {
  const path = url.split('?')[0].replace(/\/+$/, '') || '/';
  const match = path.match(/^\/(t\d+)(?:\/(.*))?$/);
  if (!match) {
    return null;
  }

  const rawPath = match[2] ?? TAB_EVENTS_PAGE;

  return {
    tabId: match[1],
    routePath: normalizeInTabRoutePath(rawPath),
  };
}

/** Normalizes legacy in-tab paths to the current scheme. */
export function normalizeInTabRoutePath(routePath: string): string {
  if (!routePath || routePath === TAB_EVENTS_PAGE) {
    return TAB_EVENTS_PAGE;
  }

  if (routePath.startsWith('e/')) {
    return `${TAB_EVENT_ID_PAGE}/${routePath.slice(2)}`;
  }

  if (routePath.startsWith('event/')) {
    return `${TAB_EVENT_ID_PAGE}/${routePath.slice('event/'.length)}`;
  }

  if (routePath.startsWith('manual-patrol/')) {
    return `${TAB_PATROL_PAGE}/${routePath.slice('manual-patrol/'.length)}`;
  }

  if (routePath.startsWith('loading/')) {
    return `${TAB_LOAD_PAGE}/${routePath.slice('loading/'.length)}`;
  }

  return routePath;
}

/** Builds router commands for a tab workspace route. */
export function buildAgentTabCommands(tabId: string, routePath = TAB_EVENTS_PAGE): string[] {
  const normalizedPath = normalizeInTabRoutePath(routePath);

  return [`/${tabId}`, ...normalizedPath.split('/').filter(Boolean)];
}

export function tabEventRoutePath(eventId: string): string {
  return `${TAB_EVENT_ID_PAGE}/${eventId}`;
}

/** In-tab path for process-event / site-check / patrol processing UIs. */
export function tabProcessingRoutePath(mode: TabProcessingRouteMode, eventId: string): string {
  if (mode === 'site-check') {
    return `${TAB_SITE_CHECK_PAGE}/${eventId}`;
  }
  if (mode === 'manual-patrol') {
    return `${TAB_PATROL_PAGE}/${eventId}`;
  }
  return tabEventRoutePath(eventId);
}

export function tabProcessingRouteSegments(
  mode: TabProcessingRouteMode,
  eventId: string,
): string[] {
  return tabProcessingRoutePath(mode, eventId).split('/').filter(Boolean);
}

/** Infers processing mode from a persisted in-tab route path. */
export function processingModeFromRoutePath(routePath: string): TabProcessingRouteMode | null {
  const normalized = normalizeInTabRoutePath(routePath);
  if (normalized.startsWith(`${TAB_SITE_CHECK_PAGE}/`)) {
    return 'site-check';
  }
  if (normalized.startsWith(`${TAB_PATROL_PAGE}/`)) {
    return 'manual-patrol';
  }
  if (normalized.startsWith(`${TAB_EVENT_ID_PAGE}/`)) {
    return 'process-event';
  }
  return null;
}

/** Event id segment from `event-id/…`, `patrol/…`, or `site-check/…` route paths. */
export function eventIdFromProcessingRoutePath(routePath: string): string | null {
  const normalized = normalizeInTabRoutePath(routePath);
  for (const prefix of [TAB_EVENT_ID_PAGE, TAB_PATROL_PAGE, TAB_SITE_CHECK_PAGE] as const) {
    if (normalized.startsWith(`${prefix}/`)) {
      const eventId = normalized.slice(prefix.length + 1).split('/')[0];
      return eventId || null;
    }
  }
  return null;
}

export function tabLoadRoutePath(action: string): string {
  return `${TAB_LOAD_PAGE}/${action}`;
}

export function isLoadingRoutePath(routePath: string): boolean {
  return routePath.startsWith(`${TAB_LOAD_PAGE}/`);
}

/** True when the in-tab path is the events dashboard landing page. */
export function isEventsLandingRoutePath(routePath: string): boolean {
  return normalizeInTabRoutePath(routePath) === TAB_EVENTS_PAGE;
}

export function loadingActionFromRoutePath(routePath: string): string | null {
  if (!isLoadingRoutePath(routePath)) {
    return null;
  }

  return routePath.slice(TAB_LOAD_PAGE.length + 1) || null;
}

export function agentTabUrl(tabId: string, routePath = TAB_EVENTS_PAGE): string {
  return buildAgentTabCommands(tabId, routePath).join('/');
}

export function isTabKey(value: string): boolean {
  return TAB_KEY_PATTERN.test(value);
}

export function formatTabKey(tabNumber: number): string {
  return `t${tabNumber}`;
}

export function nextTabNumberFromKeys(tabIds: readonly string[]): number {
  const max = tabIds.reduce((highest, tabId) => {
    const match = tabId.match(/^t(\d+)$/);
    return match ? Math.max(highest, Number(match[1])) : highest;
  }, 0);

  return max + 1;
}

/** @deprecated Use {@link parseAgentTabRoute}. */
export const parseEventsTabRoute = parseAgentTabRoute;

/** @deprecated Use {@link buildAgentTabCommands}. */
export const buildEventsTabCommands = buildAgentTabCommands;

/** @deprecated Use {@link agentTabUrl}. */
export const eventsTabUrl = agentTabUrl;

/** @deprecated Use {@link TAB_EVENT_ID_PAGE}. */
export const TAB_EVENT_SEGMENT = TAB_EVENT_ID_PAGE;

/** @deprecated Use {@link TAB_LOAD_PAGE}. */
export const TAB_LOAD_SEGMENT = TAB_LOAD_PAGE;
